import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling sidebar animation on scroll
 */
export default class SidebarAnimation extends MessageBus {
  /**
   * Constructor function of the SidebarAnimation class
   * @param {HTMLElement} element - REQUIRED - the sidebar container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;
    this.animated = false;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} SidebarAnimation A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.setupHandlers().enable();
    // Check initial position
    this.checkScroll();

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} SidebarAnimation A reference to the current instance of the class
   * @chainable
   */
  enable() {
    window.addEventListener(EVENTS.SCROLL, this.boundCheckScroll);
    return this;
  }

  /**
   * Remove event handlers
   * @returns {object} SidebarAnimation A reference to the current instance of the class
   * @chainable
   */
  disable() {
    window.removeEventListener(EVENTS.SCROLL, this.boundCheckScroll);
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.boundCheckScroll = this.checkScroll.bind(this);
    return this;
  }

  /**
   * Check if element should be animated based on scroll position
   * @returns {object} A reference to the current instance of the class
   */
  checkScroll() {
    if (this.animated) return this;

    const sidebarRect = this.element.getBoundingClientRect();
    const triggerPoint = window.innerHeight * 0.8; // 80% of viewport height

    if (sidebarRect.top <= triggerPoint) {
      this.element.classList.add(CLASS_NAMES.ANIMATE);
      this.animated = true;
      this.disable(); // Remove scroll listener after animation
    }

    return this;
  }
}
